import { type BusinessHoursHolidayHourPeriodProps } from '@/pages/OmniLocationDetails/components/AtomicField/AtomicFieldHolidayHour'
import { type BusinessHoursRegularHourPeriodProps } from '@/pages/OmniLocationDetails/components/AtomicField/AtomicFieldRegularHour'
import { isBetween } from '@/utils/functions/dates/isBetween'

export function findSameDayOverlap(
  period: [a: Date | null, b: Date | null],
  periods: Array<[a: Date | null, b: Date | null]>,
) {
  const [periodStart, periodEnd] = period
  if (!periodStart || !periodEnd) return false

  const isOverlapping = periods.some((p) => {
    const [start, end] = p
    if (!start || !end) return false

    if (start > periodEnd || end < periodStart) {
      return isBetween(periodEnd, { start, end }) || isBetween(periodStart, { start, end })
    }

    return isBetween(periodStart, { start, end }) || isBetween(periodEnd, { start, end })
  })

  return isOverlapping
}

export function findDaysOverlaps(schedule: BusinessHoursRegularHourPeriodProps[]) {
  return schedule.reduce<string[]>((overlaps, current, i) => {
    const nextIndex = (i + 1) % 7
    const next = schedule[nextIndex]

    const [startStr, endStr] = current.openingHours[0] || []
    const [nextStartStr] = next.openingHours[0] || []

    const start = startStr ? new Date(startStr) : null
    const end = endStr ? new Date(endStr) : null
    const nextStart = nextStartStr ? new Date(nextStartStr) : null

    if (!start || !end) return overlaps

    const passedToNextDay = end < start

    if (passedToNextDay && nextStart && nextStart < end) {
      return [...overlaps, next.dayOfTheWeek]
    }

    return overlaps
  }, [])
}

export function getLastDuplicateDateObjects(initialArray: BusinessHoursHolidayHourPeriodProps[]) {
  // Create a map of dates and their occurrences
  const dateMap = initialArray.reduce<Record<string, number>>((acc, item) => {
    const date =
      item.date instanceof Date
        ? item.date.toISOString().slice(0, 10)
        : String(item.date).slice(0, 10)
    acc[date] = (acc[date] || 0) + 1
    return acc
  }, {})

  // Get dates that appear more than once
  const duplicatedDates = Object.keys(dateMap).filter((date) => dateMap[date] > 1)

  if (duplicatedDates.length === 0) return []

  // Create a map to store the last object for each duplicated date
  const lastDuplicateObjects: BusinessHoursHolidayHourPeriodProps[] = []

  // Create a copy and reverse to find the last occurrences first
  const reversedArray = [...initialArray].reverse()

  // For each duplicated date, find the first occurrence in the reversed array
  duplicatedDates.forEach((dupDate) => {
    const lastObject = reversedArray.find((item) => {
      const itemDate =
        item.date instanceof Date
          ? item.date.toISOString().slice(0, 10)
          : String(item.date).slice(0, 10)
      return itemDate === dupDate
    })

    if (lastObject) {
      lastDuplicateObjects.push(lastObject)
    }
  })

  return lastDuplicateObjects
}
